/*
Created for the Produktbilder panel for the ItemCreator.
For an example usage and possible implementations of the parameters check RenderProduktbilderPanel in ItemCreator.tsx
 */

import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, IconButton, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface DragAndDropProps {
    image?: string;
    index?: number;
    onImageUpload: (images: string[]) => void;
    onDelete: () => void;
    checkImageSize?: (file: File) => void;
    sx?: object;
}

const convertAvifToBase64 = async (file: File): Promise<string> => {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  return canvas.toDataURL('image/jpeg', 1.0);
};

const DragAndDrop: React.FC<DragAndDropProps> = ({ image, index, onImageUpload, onDelete, checkImageSize, sx }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isCtrlPressed, setIsCtrlPressed] = useState(false);

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: index,
        disabled: !isCtrlPressed,
      animateLayoutChanges: () => false,
    });

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Control") {
                setIsCtrlPressed(true);
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            if (event.key === "Control") {
                setIsCtrlPressed(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    const defaultStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: "100%",
        height: "auto",
        aspectRatio: "1.6/1",
        border: "2px dashed gray",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isCtrlPressed ? "grab" : "pointer",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        backgroundColor: isHovered ? "lightgray" : "transparent",
    };

    const onDrop = (acceptedFiles: File[]) => {
        const imageFiles = acceptedFiles.filter(file => file.type.startsWith("image/"));
        imageFiles.forEach((file) => {
            checkImageSize?.(file); // Only call if checkImageSize is provided
        });


      const newImages = imageFiles.map(file => {
        if (file.type === 'image/avif') {
          return convertAvifToBase64(file);
        }
        return new Promise<string>(resolve => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        })
      });

        Promise.all(newImages).then(images => onImageUpload(images));
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { "image/*": [] },
        multiple: true,
    });

    return (
        <Box
            {...getRootProps()}
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{ ...defaultStyle, ...sx }}
        >
            <input {...getInputProps()} />
            {image ? (
                <>
                    <img
                        src={image}
                        alt="Uploaded"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            position: "absolute",
                            top: 0,
                            left: 0,
                        }}
                    />
                    {isHovered && (
                        <IconButton
                            onClick={e => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                            }}
                        >
                            <DeleteOutlineIcon color="error" />
                        </IconButton>
                    )}
                </>
            ) : (
                <Typography variant="body1" color="textSecondary">
                    +
                </Typography>
            )}
        </Box>
    );
};

export default DragAndDrop;
